/**
 * Format seconds as HH:MM:SS.mmm — must match createVideo.jsx's secondsToHMS,
 * since the Time column renders the `duration` [startStr, endStr] pair. Any
 * re-cut must rebuild `duration` or it leaks the original sentence's timestamp.
 */
function toHMS(totalSeconds) {
  const t = totalSeconds || 0;
  const ms = Math.round((t % 1) * 1000);
  const s = Math.floor(t) % 60;
  const m = Math.floor(t / 60) % 60;
  const h = Math.floor(t / 3600) % 24;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

/**
 * Merge consecutive sentence-level clips into larger segments.
 * This is a pure frontend transform — originalRows is never mutated.
 *
 * Rules (applied in order per sentence):
 *  1. If the speaker changes → hard break (never merge across speakers)
 *  2. If the silence gap to the next sentence > maxGap  → hard break, start new group
 *  3. If combined text > maxChars AND current group already meets minDuration → hard break
 *  4. Otherwise → merge into current group
 */
export function regroupSentences(sentences, { maxChars = 9999, minDuration = 0, maxGap = 30 }) {
  const sorted = [...sentences]
    .filter((s) => !s.space)
    .sort((a, b) => (a.start_in || 0) - (b.start_in || 0));

  if (!sorted.length) return sentences;

  const groups = [];
  let cur = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const s = sorted[i];
    const lastInGroup = cur[cur.length - 1];
    const gap = Math.max(0, (s.start_in || 0) - (lastInGroup.end_in || 0));
    const combinedLen = cur.reduce((sum, x) => sum + (x.sentence || "").length + 1, 0) + (s.sentence || "").length;
    const groupDuration = (lastInGroup.end_in || 0) - (cur[0].start_in || 0);

    // Never merge clips from different speakers
    const speakerChanged = !!(s.speaker && lastInGroup.speaker && s.speaker !== lastInGroup.speaker);
    const gapTooLarge = gap > maxGap;
    const charsTooLong = combinedLen > maxChars;
    const meetsMinDuration = groupDuration >= minDuration;

    if (speakerChanged || gapTooLarge || (charsTooLong && meetsMinDuration)) {
      groups.push(cur);
      cur = [s];
    } else {
      cur.push(s);
    }
  }
  groups.push(cur);

  // Build the merged row objects — shape matches a regular sentence row
  return groups.map((group) => {
    if (group.length === 1) return group[0];

    const first = group[0];
    const last = group[group.length - 1];
    const text = group.map((s) => (s.sentence || "").trim()).join(" ");

    // Dominant sentiment
    const sentCounts = {};
    for (const s of group) {
      const k = s.sentiment || "neutral";
      sentCounts[k] = (sentCounts[k] || 0) + 1;
    }
    const sentiment = Object.entries(sentCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "neutral";

    const duration = (last.end_in || 0) - (first.start_in || 0);

    return {
      ...first,
      sentence: text,
      start_in: first.start_in,
      end_in: last.end_in,
      duration: [toHMS(first.start_in), toHMS(last.end_in)],
      duration_ms: Math.round(duration * 1000),
      sentiment,
      speaker: first.speaker,
      question: group.some((s) => s.question),
      sec: duration,
      sec_int: Math.ceil(duration),
      sec_float: parseFloat(duration.toFixed(2)),
      length: text.length,
      words: group.reduce((sum, s) => sum + (s.words || 0), 0),
      utterance: group.flatMap((s) => s.utterance || []),
      _merged: true,
      _mergedCount: group.length,
      _mergedIds: group.map((s) => s.id),
    };
  });
}

/**
 * Flatten sentences into individual words with timings. Each sentence's
 * `.utterance` is an array of [start, end] aligned with its words; if it's
 * missing we distribute the words evenly across the sentence's duration.
 */
function flattenWords(clips) {
  const words = [];
  for (const s of clips) {
    const toks = (s.sentence || "").trim().split(/\s+/).filter(Boolean);
    if (!toks.length) continue;
    const utt = s.utterance || [];
    const sStart = s.start_in || 0;
    const sEnd = s.end_in != null ? s.end_in : sStart;
    const per = toks.length ? (sEnd - sStart) / toks.length : 0;
    for (let i = 0; i < toks.length; i++) {
      const timing = utt[i];
      const start = timing ? timing[0] : sStart + i * per;
      const end = timing ? timing[1] : start + per;
      words.push({ text: toks[i], start, end, thumbnail: s.thumbnail, sentiment: s.sentiment, speaker: s.speaker, srcId: s.id });
    }
  }
  return words.sort((a, b) => a.start - b.start);
}

const nearestThumbFactory = (clips) => {
  const thumbs = clips.filter((s) => s.thumbnail).map((s) => ({ t: s.start_in || 0, thumb: s.thumbnail }));
  return (t) => {
    if (!thumbs.length) return undefined;
    let best = thumbs[0], bestD = Math.abs(thumbs[0].t - t);
    for (const e of thumbs) { const d = Math.abs(e.t - t); if (d < bestD) { best = e; bestD = d; } }
    return best.thumb;
  };
};

/**
 * Re-slice the whole timeline into fixed N-second clips, regardless of sentences
 * or speakers. Every window [t, t+N) becomes one clip whose TEXT is only the
 * words actually spoken in that slice — so the text shrinks/grows with N.
 */
export function regroupByInterval(sentences, intervalSeconds = 5) {
  const clips = [...sentences]
    .filter((s) => !s.space)
    .sort((a, b) => (a.start_in || 0) - (b.start_in || 0));
  if (!clips.length) return sentences;

  const N = Math.max(0.5, Number(intervalSeconds) || 5);
  const tStart = clips[0].start_in || 0;
  const tEnd = clips.reduce((m, s) => Math.max(m, s.end_in || 0), 0);
  const words = flattenWords(clips);
  const nearestThumb = nearestThumbFactory(clips);
  const baseRow = clips[0];

  const out = [];
  let w = 0;
  for (let t = tStart; t < tEnd - 1e-6; t += N, w++) {
    const wEnd = Math.min(t + N, tEnd);
    const inWin = words.filter((x) => x.start < wEnd && x.end > t);
    const text = inWin.map((x) => x.text).join(" ");

    const sc = {};
    inWin.forEach((x) => { const k = x.sentiment || "neutral"; sc[k] = (sc[k] || 0) + 1; });
    const sentiment = Object.entries(sc).sort((a, b) => b[1] - a[1])[0]?.[0] || "neutral";

    const duration = wEnd - t;
    out.push({
      ...baseRow,
      id: `iv-${w}`,
      sentence: text,
      start_in: t,
      end_in: wEnd,
      duration: [toHMS(t), toHMS(wEnd)],
      duration_ms: Math.round(duration * 1000),
      sentiment,
      speaker: inWin[0]?.speaker || baseRow.speaker,
      question: false,
      sec: duration,
      sec_int: Math.ceil(duration),
      sec_float: parseFloat(duration.toFixed(2)),
      length: text.length,
      thumbnail: nearestThumb(t),
      utterance: inWin.map((x) => [x.start, x.end]),
      space: false,
      _interval: true,
      // the original sentence ids whose words fall in this window — used to average virality
      _srcIds: Array.from(new Set(inWin.map((x) => x.srcId).filter((v) => v != null))),
    });
  }
  return out;
}

/**
 * Break every sentence into one clip per word — the finest segmentation.
 * Uses word timings from `.utterance`, falling back to an even split.
 */
export function splitIntoWords(sentences) {
  const clips = [...sentences]
    .filter((s) => !s.space)
    .sort((a, b) => (a.start_in || 0) - (b.start_in || 0));
  if (!clips.length) return sentences;

  const out = [];
  let w = 0;
  for (const s of clips) {
    const toks = (s.sentence || "").trim().split(/\s+/).filter(Boolean);
    if (!toks.length) continue;
    const utt = s.utterance || [];
    const sStart = s.start_in || 0;
    const sEnd = s.end_in != null ? s.end_in : sStart;
    const per = toks.length ? (sEnd - sStart) / toks.length : 0;
    for (let i = 0; i < toks.length; i++) {
      const timing = utt[i];
      const start = timing ? timing[0] : sStart + i * per;
      const end = timing ? timing[1] : start + per;
      const dur = Math.max(0, end - start);
      out.push({
        ...s,
        id: `w-${w++}`,
        sentence: toks[i],
        start_in: start,
        end_in: end,
        duration: [toHMS(start), toHMS(end)],
        duration_ms: Math.round(dur * 1000),
        sec: dur,
        sec_int: Math.ceil(dur),
        sec_float: parseFloat(dur.toFixed(2)),
        length: toks[i].length,
        utterance: [[start, end]],
        space: false,
        question: false,
        _word: true,
        _wordOfId: s.id,
      });
    }
  }
  return out;
}
